import { useCallback, useRef, useState } from 'react'
import httpClient from '../utility/httpClient.util'
import { waitForIceGathering } from '../utility/webrtcRecorder.util'

export const WEBRTC_RECORDER_STATUS = {
  idle: 'idle',
  starting: 'starting',
  recording: 'recording',
  stopping: 'stopping',
  error: 'error',
}

const DEFAULT_BASE_PATH = '/teleconsult/recordings'
const DEFAULT_ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }]

export default function useWebRtcRecorder({ profile, basePath = DEFAULT_BASE_PATH } = {}) {
  const pcRef = useRef(null)
  const recordingIdRef = useRef(null)
  const [status, setStatus] = useState(WEBRTC_RECORDER_STATUS.idle)
  const [error, setError] = useState(null)

  const teardownPeer = useCallback(() => {
    if (pcRef.current) {
      try {
        pcRef.current.close()
      } catch (closeError) {
        setError(closeError)
      }
      pcRef.current = null
    }
  }, [])

  const start = useCallback(
    async (stream) => {
      if (
        status === WEBRTC_RECORDER_STATUS.recording ||
        status === WEBRTC_RECORDER_STATUS.starting
      ) {
        return
      }
      setStatus(WEBRTC_RECORDER_STATUS.starting)
      setError(null)
      try {
        const { data: body } = await httpClient.post(basePath, { profile })
        const { recordingId, offerSdp, iceServers } = (body && body.data) || {}
        if (!recordingId || !offerSdp) throw new Error('No recording offer from server')
        recordingIdRef.current = recordingId

        const pc = new RTCPeerConnection({
          iceServers:
            Array.isArray(iceServers) && iceServers.length
              ? iceServers
              : DEFAULT_ICE_SERVERS,
        })
        pcRef.current = pc
        stream.getAudioTracks().forEach((track) => pc.addTrack(track, stream))

        await pc.setRemoteDescription(new RTCSessionDescription(offerSdp))
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        await waitForIceGathering(pc)

        await httpClient.put(`${basePath}/${recordingId}/answer`, {
          answerSdp: { sdp: pc.localDescription.sdp, type: pc.localDescription.type },
        })
        setStatus(WEBRTC_RECORDER_STATUS.recording)
      } catch (startError) {
        setError(startError)
        setStatus(WEBRTC_RECORDER_STATUS.error)
        teardownPeer()
        recordingIdRef.current = null
        throw startError
      }
    },
    [status, profile, basePath, teardownPeer],
  )

  const stop = useCallback(async () => {
    const recordingId = recordingIdRef.current
    if (!recordingId) return null
    setStatus(WEBRTC_RECORDER_STATUS.stopping)
    try {
      const { data: body } = await httpClient.put(`${basePath}/${recordingId}/stop`)
      const attachmentId = (body && body.data && body.data.audioAttachmentId) || null
      teardownPeer()
      recordingIdRef.current = null
      setStatus(WEBRTC_RECORDER_STATUS.idle)
      return attachmentId
    } catch (stopError) {
      setError(stopError)
      setStatus(WEBRTC_RECORDER_STATUS.error)
      teardownPeer()
      recordingIdRef.current = null
      throw stopError
    }
  }, [basePath, teardownPeer])

  const cancel = useCallback(async () => {
    const recordingId = recordingIdRef.current
    teardownPeer()
    recordingIdRef.current = null
    setStatus(WEBRTC_RECORDER_STATUS.idle)
    if (recordingId) {
      try {
        await httpClient.delete(`${basePath}/${recordingId}`)
      } catch (cancelError) {
        setError(cancelError)
      }
    }
  }, [basePath, teardownPeer])

  return { status, error, start, stop, cancel }
}
